import React from 'react'
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setP2pTrader } from '../../request/CoinRates/coinSlice';
import { useDispatch } from 'react-redux';

import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { yellow } from '@mui/material/colors';
import { Fragment } from 'react';

const Sell = () => {
  const advertisers = useSelector((state) => state.coins.addresses);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const formatPrice = (amount) => new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    currencySign: 'standard',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  const handleTraderClick = (advertiser) => {
    dispatch(setP2pTrader(advertiser));
    setTimeout(() => {
      navigate('/seller');
    }, 100);
  };

  return (
    <Box sx={{ width: '100%', mt: 2, px: isMobile ? 1 : 3 }}>
      {advertisers.length > 0 && (
        <List sx={{ width: '100%', bgcolor: 'transparent' }}>
          {advertisers.map((advertiser) => (
            (advertiser.trader_name !== null && advertiser.display_area === 'withdrawals') && (
              <Fragment key={advertiser.id}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out'
                    }
                  }}
                >
                  <ListItem 
                    alignItems="flex-start" 
                    sx={{ 
                      cursor: 'pointer',
                      p: isMobile ? 2 : 3,
                      flexDirection: isMobile ? 'column' : 'row',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                      }
                    }}
                    onClick={() => handleTraderClick(advertiser)}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      width: '100%', 
                      alignItems: 'center',
                      mb: isMobile ? 2 : 0
                    }}>
                      <ListItemAvatar>
                        <Avatar
                          sx={{ 
                            bgcolor: yellow[700], 
                            width: isMobile ? 40 : 48, 
                            height: isMobile ? 40 : 48,
                            fontSize: isMobile ? '1.1rem' : '1.3rem',
                            fontWeight: 600,
                            boxShadow: 2
                          }}
                          alt={advertiser.trader_name}
                        >
                          {advertiser.trader_name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>

                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        ml: 2,
                        flex: 1
                      }}>
                        <Typography 
                          variant={isMobile ? "h6" : "h5"} 
                          component="div" 
                          sx={{ 
                            fontWeight: 600,
                            mb: 1,
                            color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main'
                          }}
                        >
                          {advertiser.trader_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Chip 
                            label={`${advertiser.orders} Orders`} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                            sx={{ 
                              height: 28,
                              fontWeight: 500,
                              fontSize: isMobile ? '0.75rem' : '0.8125rem'
                            }}
                          />
                          <Chip 
                            label={`${parseInt(advertiser.percentage)}%`} 
                            size="small" 
                            color="success"
                            variant="outlined"
                            sx={{ 
                              height: 28,
                              fontWeight: 500,
                              fontSize: isMobile ? '0.75rem' : '0.8125rem'
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    <Grid container spacing={3} sx={{ mt: isMobile ? 2 : 0 }}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary'
                            }}
                          >
                            <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Quantity:</Box>
                            {formatPrice(advertiser.quantity)}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary'
                            }}
                          >
                            <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Limits:</Box>
                            {formatPrice(advertiser.min)} - {formatPrice(advertiser.max)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary'
                            }}
                          >
                            <Box component="span" sx={{ color: 'text.secondary' }}>Payment Method:</Box>
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 400,
                              color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'
                            }}
                          >
                            {advertiser.payment_description}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box sx={{ 
                      width: '100%', 
                      mt: isMobile ? 3 : 0,
                      display: 'flex',
                      justifyContent: isMobile ? 'center' : 'flex-end'
                    }}>
                      <Button 
                        variant="contained" 
                        color="error"
                        size={isMobile ? "medium" : "large"}
                        sx={{ 
                          fontWeight: 600,
                          minWidth: isMobile ? '120px' : '140px',
                          boxShadow: 2,
                          py: 1,
                          px: 3,
                          fontSize: isMobile ? '0.875rem' : '1rem',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        Sell
                      </Button>
                    </Box>
                  </ListItem>
                </Paper>
              </Fragment>
            )
          ))}
        </List>
      )}
    </Box>
  );
};

export default Sell;
